import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, paid, failed
  const [attempts, setAttempts] = useState(0);
  const [userEmail, setUserEmail] = useState("");
  const [packageId, setPackageId] = useState("");
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const pollRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let cancelled = false;
    const maxAttempts = 10;

    const poll = async (n) => {
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.user_email) setUserEmail(data.user_email);
        if (data.package_id) setPackageId(data.package_id);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refreshUser();
          return;
        }
        if (data.status === "expired") {
          setStatus("failed");
          return;
        }
        if (n >= maxAttempts) {
          setStatus("failed");
          return;
        }
        setAttempts(n);
        pollRef.current = setTimeout(() => poll(n + 1), 2000);
      } catch {
        if (!cancelled) setStatus("failed");
      }
    };
    poll(1);
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [sessionId, refreshUser]);

  const productMap = {
    menopause_invitation: { name: "The Menopause Invitation", dashboard: "/dashboard/menopause", cancel: "/the-menopause-invitation" },
    mens_guide: { name: "A Man's Guide to Her Menopause", dashboard: "/dashboard/mens-guide", cancel: "/a-mans-guide-to-her-menopause" },
    making_fabulous: { name: "The Rewired Life", dashboard: "/dashboard/the-rewired-life", cancel: "/the-rewired-life" },
  };
  const productInfo = productMap[packageId] || productMap.making_fabulous;
  const productName = productInfo.name;
  const dashboardPath = productInfo.dashboard;

  return (
    <section className="min-h-[70vh] flex items-center py-24 md:py-32" data-testid="page-checkout-success">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <SectionReveal>
          {status === "checking" && (
            <>
              <Loader2 className="mx-auto mb-8 text-sage animate-spin" size={40} strokeWidth={1.3} />
              <h1 className="font-serif-display text-4xl md:text-5xl text-ink">Confirming your payment…</h1>
              <p className="mt-5 text-ink-soft">Hang tight. This usually takes 5–10 seconds. (Attempt {attempts})</p>
            </>
          )}
          {status === "paid" && (
            <>
              <div className="mx-auto mb-8 w-20 h-20 rounded-full bg-sage text-cream inline-flex items-center justify-center">
                <Check size={32} strokeWidth={1.3} />
              </div>
              <div className="eyebrow mb-4 text-sage">You're in.</div>
              <h1 className="font-serif-display text-4xl md:text-6xl text-ink leading-tight">
                Welcome to <em className="italic text-sage">{productName}</em>.
              </h1>
              <p className="mt-6 text-ink-soft max-w-lg mx-auto leading-relaxed">
                Your lifetime access is ready. Take a breath. The next right step is already waiting for you inside.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                {user && user !== false ? (
                  <Link
                    to={dashboardPath}
                    className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium transition-all"
                    data-testid="success-dashboard-cta"
                  >
                    Enter your dashboard
                    <ArrowRight size={16} strokeWidth={1.8} />
                  </Link>
                ) : (
                  <>
                    <button
                      onClick={() => navigate(`/register${userEmail ? `?email=${encodeURIComponent(userEmail)}` : ""}`)}
                      className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-8 py-4 font-medium transition-all"
                      data-testid="success-register-cta"
                    >
                      Set up your account
                      <ArrowRight size={16} strokeWidth={1.8} />
                    </button>
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-2 border border-sage text-sage rounded-full px-8 py-4 font-medium"
                      data-testid="success-login-cta"
                    >
                      I already have one
                    </Link>
                  </>
                )}
              </div>
              {!user && (
                <p className="mt-6 text-sm text-ink-soft">
                  Use the same email address you used at checkout to unlock access automatically.
                </p>
              )}
            </>
          )}
          {status === "failed" && (
            <>
              <h1 className="font-serif-display text-4xl md:text-5xl text-ink">We couldn't confirm your payment.</h1>
              <p className="mt-5 text-ink-soft">
                If you were charged, please email Pip directly and she'll sort it out personally.
              </p>
              <Link
                to={productInfo.cancel}
                className="mt-8 inline-flex items-center gap-2 bg-sage text-cream rounded-full px-8 py-4 font-medium"
                data-testid="success-retry-cta"
              >
                Back to {productName}
              </Link>
            </>
          )}
        </SectionReveal>
      </div>
    </section>
  );
}
